import api from 'api';
import { createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import { IGetUserAPIParams, IClientUser } from './userSlice';
import { convertDropdownList, usersRoles } from 'helpers/utils';
import { IOrganizationAddress } from './organizationSlice';
import { getItemFromLocalStorage } from 'helpers/localstorage';
import { IAuthOrganizations } from 'interfaces/auth-pages-interfaces';
import { IAddress } from 'interfaces/common-interfaces';

export interface IAdminRoleUserDetails {
  user_details: { email: string; first_name: string; id: string; last_name: string };
  role_details: {
    is_deleted: boolean;
    name: string;
    permission_id: string;
    _id: string;
  }[];
  org_data: { organization_id: string; organization_name: string };
  parking_details: IAddress[];
}
export interface IAdminRoleUser {
  _id?: string;
  details: IAdminRoleUserDetails[];
}

export interface IRoleDetails {
  company_id?: string;
  organization: IOrganizationAssociateParking | null;
  role: IRoles | null;
  isEdited?: boolean;
}
export interface IAdminstratorRole {
  _id?: string;
  id?: string;
  email: string;
  associated_company?: string;
  name: string;
  surname: string;
  roleDetails: IRoleDetails[];
  role: IRoles | null;
  company_id?: string;
  details?: IAdminRoleUserDetails[];
  user_role_id?: string;
}
export interface IRoles {
  id: string;
  name: string;
  _id: string;
  value: string;
}
export interface IAddAdminRoleParam
  extends Omit<
    IClientUser,
    | 'dni_no'
    | 'dni_exp_date'
    | 'license_no'
    | 'license_exp_date'
    | 'billing_address'
    | 'policy_no'
    | 'policy_exp_date'
    | 'type_of_purchase_ids'
  > {
  organization_id: string;
  organization_name: string;
  role: string;
  role_id: string;
}
export interface IAdminRoleParkingAddress extends IOrganizationAddress {
  isSelected?: boolean;
}
export interface IOrganizationAssociateParking {
  name: string;
  organization_name: string;
  _id: string;
  id: string;
  parking: IAdminRoleParkingAddress[];
}
interface IAdminRolesState {
  adminRoles: IAdminRoleUser[];
  roles: IRoles[];
  organizationAssociateParking: IOrganizationAssociateParking[];
  adminRoleDetails: IAdminRoleUser | IAdminstratorRole | null;
  totalAdminCount: number;
  totalUserCount: number;
}

export interface IRoleDataRequest {
  role: string;
  organization_id?: string;
  parking_ids: string[];
  company_id?: string;
}

export interface IRoleDataRequestForFleet {
  user_role_id?: string;
  role: string;
  organization_id?: string;
  company_id: string;
}
export interface IAddEditAdminRoleRequest {
  user_id?: string;
  name: string;
  surname: string;
  email: string;
  role_details: IRoleDataRequest[] | IRoleDataRequestForFleet;
  company_id?: string;
}
interface IAdminRoleUserResponse {
  response: IAdminRoleUser[];
  record_count: number;
}
interface IUserRoleResponse {
  allRoles: IRoles[];
  record_count: number;
}
const initialState: IAdminRolesState = {
  adminRoles: [],
  roles: [],
  organizationAssociateParking: [],
  adminRoleDetails: null,
  totalAdminCount: 0,
  totalUserCount: 0,
};
const adminiStratorRolesSlice = createSlice({
  name: 'Administrator Roles',
  initialState: initialState,
  reducers: {
    setAdminRoles: (state, { payload }: { payload: IAdminRoleUserResponse }) => {
      state.adminRoles = [...payload.response];
      state.totalAdminCount = payload.record_count;
    },
    setUserRolesList: (state, { payload }: { payload: IUserRoleResponse }) => {
      state.roles = [...payload.allRoles];
      state.totalUserCount = payload.record_count;
    },
    setOrganizationAndParking: (
      state,
      { payload }: { payload: IOrganizationAssociateParking[] },
    ) => {
      state.organizationAssociateParking = [...payload];
    },
    setAdminRoleDetails: (
      state,
      { payload }: { payload: IAdminstratorRole | IAdminRoleUser | null },
    ) => {
      state.adminRoleDetails = payload;
    },
  },
});

export const getDealerAdminRoles =
  (
    payload: { params?: IGetUserAPIParams; url: string; offset?: number; limit?: number },
    signal?: AbortSignal,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { params, url, offset, limit } = payload;
      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}`;

      const { data: responseData } = await api.get(`/admin/org/admin-users${url}${queryString}`, {
        params,
        signal,
      });

      dispatch(setAdminRoles(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getSuperAdminRoles =
  (
    payload: { params?: IGetUserAPIParams; url: string; offset?: number; limit?: number },
    signal?: AbortSignal,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { params, url, offset, limit } = payload;
      let queryString;
      if (!url) {
        queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}`;
      } else {
        queryString = `&offset=${offset ?? 0}&limit=${limit ?? 20}`;
      }
      const apiUrl = `/admin/admin-users${url}${queryString}`;
      const { data: responseData } = await api.get(apiUrl, {
        params,
        signal,
      });
      dispatch(setAdminRoles(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const getUserRolesList = () => async (dispatch: Dispatch) => {
  try {
    const { data: responseData } = await api.get('/admin/roles');

    dispatch(setUserRolesList(responseData?.data));
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addSuperAdminUser =
  (payload: { request: IAddEditAdminRoleRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.post('/admin/users', request);
      return Promise.resolve(responseData?.data?.response);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getOrganizationListWithAssociateParking =
  (payload: { params: { organization_id?: string } }) => async (dispatch: Dispatch) => {
    try {
      const localOrg: IAuthOrganizations = getItemFromLocalStorage('org');
      const { params } = payload;
      const { data: responseData } = await api.get('/admin/org/parkingDetail', { params });
      let updatedList: IOrganizationAssociateParking[] = responseData;
      if (localOrg.role_name === usersRoles?.DEALER_ADMIN) {
        updatedList = [
          {
            _id: localOrg?.organization_id || '',
            name: localOrg?.organization_name || '',
            organization_name: localOrg?.organization_name || '',
            id: localOrg?.organization_id || '',
            parking: responseData?.data[0].parking ? responseData?.data[0].parking : [],
          },
        ];
      } else {
        updatedList = convertDropdownList(responseData?.data, '_id', 'id');
        updatedList = convertDropdownList(updatedList, 'organization_name', 'name');
      }
      dispatch(setOrganizationAndParking(updatedList));
      return Promise.resolve(updatedList);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getAdminRoleDetails = (payload: { user_id: string }) => async (dispatch: Dispatch) => {
  try {
    const { user_id } = payload;
    const {
      data: responseData,
    }: {
      data: {
        data: {
          role_details: {
            orgDetails: { organization_name: string; _id: string };
            parkings: IAdminRoleParkingAddress[];
            roleDetails: IRoles;
            company_id?: string;
            _id?: string;
          }[];
          user_details: IAdminstratorRole;
        };
      };
    } = await api.get(`/admin/org/user/${user_id}`);
    const formattedData: IAdminstratorRole = {
      id: responseData?.data?.user_details?._id,
      name: responseData?.data?.user_details?.name,
      surname: responseData?.data?.user_details?.surname,
      email: responseData?.data?.user_details?.email,
      associated_company:
        responseData?.data?.role_details[0].roleDetails.name === usersRoles?.CLIENT
          ? responseData?.data?.user_details?.company_id ||
            responseData?.data?.user_details?.associated_company
          : responseData?.data?.role_details[0].company_id,

      roleDetails: responseData?.data?.role_details.map(item => {
        return {
          role: item?.roleDetails,
          organization: {
            _id: item?.orgDetails?._id,
            id: item?.orgDetails?._id,
            organization_name: item?.orgDetails?.organization_name,
            name: item?.orgDetails?.organization_name,
            parking: item?.parkings.map(parking => {
              return { ...parking, isSelected: true };
            }),
          },
        };
      }),
      role: responseData?.data?.role_details[0].roleDetails,
      user_role_id: responseData?.data?.role_details[0]._id || '',
    };
    dispatch(setAdminRoleDetails(formattedData));
    return Promise.resolve(formattedData);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const editAdminUser =
  (payload: { request: IAddEditAdminRoleRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.post(`/admin/users/${request?.user_id}`, request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const deleteAdminUser = (payload: { user_id: string }) => async (dispatch: Dispatch) => {
  try {
    const { user_id } = payload;
    const { data: responseData } = await api.delete(`/admin/user/${user_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const addFleetManagerAdminUser =
  (payload: { request: IAddEditAdminRoleRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.post('/admin/fleet/user', request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const editFleetManagerAdminUser =
  (payload: { user_id: string; request: IAddEditAdminRoleRequest }) =>
  async (dispatch: Dispatch) => {
    try {
      const { request, user_id } = payload;
      const { data: responseData } = await api.post(`/admin/fleet/user/${user_id}`, request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const { setAdminRoles, setUserRolesList, setOrganizationAndParking, setAdminRoleDetails } =
  adminiStratorRolesSlice.actions;
export default adminiStratorRolesSlice.reducer;
